/**
 * CONFIGURACIÓN DE FEATURE FLAGS
 * 
 * IMPORTANTE:
 * - En desarrollo (DEV_MODE = true): TODOS los módulos habilitados
 * - En producción (DEV_MODE = false): Módulos según plan del cliente
 */

// Modo desarrollo real según entorno Vite.
export const DEV_MODE = import.meta.env.DEV;

// Módulos disponibles en el sistema
export type FeatureName = 
  | 'pos'           // POS (siempre habilitado - plan básico)
  | 'kds'           // KDS (siempre habilitado - plan básico)
  | 'ecommerce'     // Web de Pedidos
  /*
   * 🪤 «PEDIDOS» GATEABA CON `ecommerce`, Y ESA PREMISA DEJÓ DE SER CIERTA.
   *    El razonamiento era «sin tienda no hay pedidos web», y valía mientras la única web fuera la
   *    de Smarty. Pero existe la integración Shopify —OAuth, webhook `orders/create` y
   *    sincronización de carritos abandonados— y está pensada, textual, «para merchants que venden
   *    en Shopify y NO quieren migrar la tienda». Su ruta no pide ninguna feature: cualquier plan
   *    la puede conectar hoy.
   *
   *    O sea que un cliente podía enchufar Shopify, empezar a recibir pedidos y carritos
   *    abandonados, y no tener dónde verlos — porque la pantalla colgaba de un módulo que él no
   *    tiene. Lo que gatea Pedidos no es tener tienda propia: es tener UN CANAL WEB, sea de quien
   *    sea.
   *
   * 🪤 SIGUE FUERA DE `drop` A PROPÓSITO. Un revendedor no tiene canal web de ningún tipo, y darle
   *    la entrada le pone dos pantallas siempre vacías al lado de «Ventas» y «Mis compras» — tres
   *    cosas que suenan igual. Ver el comentario del ítem en navigation.config.ts.
   */
  | 'pedidos_online' // Pedidos y carritos que entran por un canal web (tienda propia o Shopify)
  /*
   * 🪤 MARKETPLACES COLGABA DE `ecommerce`, Y `ecommerce` LO TIENE HASTA EL BÁSICO. Vender en
   *    Mercado Libre y Falabella es un canal que se paga (Pro y Enterprise): el Básico, el Drop y el
   *    almacén no lo tienen, y ofrecérselo es mostrarles algo que no pueden usar. Además el menú sólo
   *    lo muestra cuando la cuenta tiene uno de los dos integrado (ver el Sidebar).
   */
  | 'marketplaces' // Mercado Libre y Falabella: publicar, stock y pedidos (Pro y Enterprise)
  | 'loyalty'       // Fidelización & Wallet
  | 'cod'           // Pedidos contra reembolso (Formulario COD, configuración)
  | 'operaciones'   // Operaciones: Ventas, Logística, Cobranzas (módulo por plan)
  | 'crm'           // CRM: contactos, pipeline, tareas, secuencias
  /*
   * 🪤 LLAMADAS VIAJABA DENTRO DE `crm`, Y ESO IMPEDÍA VENDER EL CRM SIN EL CALL CENTER.
   *    Una sola feature encendía DOS entradas de menú —«CRM» y «Llamadas»— y 23 pantallas. Al
   *    querer salir con el pipeline y nada más, no había forma de dejar afuera el marcador, la voz
   *    IA y el coaching: el interruptor era uno solo.
   *
   *    Se separa el 1-sep-2026. `call_center` nace en `development`, así que las cuentas internas lo
   *    siguen viendo entero y el cliente no lo ve hasta que esté terminado — que es exactamente lo
   *    que `retirada` NO puede hacer, porque esconde para todos por igual.
   */
  | 'call_center'   // Llamadas: marcador, campañas de voz, Voice AI, coaching
  | 'marketing'     // Marketing & Upselling
  | 'logistics'     // Delivery Propio
  | 'proveedores'   // Catálogos de proveedores externos (Dropi, Aliclik)
  | 'commander'     // IA & Chat
  | 'finance'       // Control de Caja
  | 'inventory'     // Existencias Avanzado
  | 'whatsapp'      // WhatsApp (inbox omnicanal, canal por plan)
  | 'chatbot'       // Chatbot Builder (flujos, AI, widget)
  | 'ads'           // Smarty Ads (campañas Meta con IA)
  | 'accounting'    // Contabilidad (comprobantes SUNAT/CPE)
  | 'agents'        // Agentes IA (studio de agentes)
  | 'copilot'       // Copiloto operativo IA (agente que opera el negocio) — solo plan top
  | 'discounts'     // Descuentos y cupones
  /*
   * 🪤 LOS AVISOS ESTABAN, PERO ENTERRADOS. La pantalla que mapea eventos → grupos de WhatsApp vivía
   *    en Ajustes bajo «Notificaciones por WhatsApp», gateada con `whatsapp`. Y el problema no era
   *    el acceso: Dulti tiene SEIS líneas de WhatsApp conectadas y CERO reglas configuradas — tenía
   *    la pantalla y no la usó. De nueve comercios, uno solo tenía alguna.
   *
   *    Un nombre que suena a «configuración de WhatsApp» esconde lo que en realidad es: enterarte
   *    cuando vendés. Pasa a módulo propio, con su entrada en el menú.
   *
   * ⭐ VA EN LOS CUATRO PLANES, DROP INCLUIDO. Un revendedor también vende, y también quiere
   *    enterarse. No hay una versión del negocio a la que no le sirva.
   */
  | 'avisos'        // Avisos: qué eventos del negocio notifican, y por dónde
  /*
   * Las tres de abajo nacieron el 25-ago-2026 separando lo que el plan Drop NO usa. Antes no
   * hacían falta porque TODOS los planes las tenían: la distinción no existía, así que el gate no
   * faltaba — sobraba la coincidencia. Es el mismo caso de `ecommerce` en Contenido y Marketplaces.
   */
  | 'catalogo_propio' // Administra SU catálogo: variantes, existencias, ofertas, modificadores
  | 'analiticas'      // Analíticas y reportes de negocio
  | 'flota_propia';   // Despacha con gente propia y recauda: motorizados, rendimiento del recaudo

/**
 * Piso de emergencia: lo que se enciende cuando NO se pudo averiguar el plan.
 *
 * 🪤 NO ES UN PISO PARA TODAS LAS CUENTAS, y hasta el 2026-08-23 lo era: se sumaba
 *    incondicionalmente al plan, así que **ninguna cuenta podía tener menos que POS + KDS**.
 *    Eso hacía imposible un plan más chico que `basic` — y el plan Drop es exactamente eso.
 *
 *    Sacarlo de la ruta feliz no cambia nada para las cuentas que hay: `basic`, `pro` y
 *    `enterprise` ya declaran `pos` y `kds` en su propia lista, tanto en el código como en la
 *    tabla `plans`. Se comprobó contra prod antes de tocarlo. Sigue mandando en las ramas de
 *    error, que es donde tiene sentido: sin plan legible, una cuenta usable es mejor que una
 *    cuenta muerta.
 */
export const BASIC_FEATURES: FeatureName[] = ['pos', 'kds'];

// Planes y sus features
export const PLAN_FEATURES: Record<string, FeatureName[]> = {
  // BASIC = el producto core: una tienda que VENDE de verdad.
  //
  // Antes era solo ['pos','kds'], y eso dejaba la cuenta inutilizable: `create-store` crea
  // todos los tenants nuevos con plan 'basic', así que un cliente que se registraba quedaba
  // SIN Pagos, SIN Checkout, SIN Envíos, SIN Dominios, SIN Marca (todo eso lo gatea
  // `ecommerce`), SIN ver sus pedidos (`operaciones`), sin formulario COD y sin Transporte.
  // Tenía la tienda provisionada y no podía usarla.
  //
  // Cada una es un bloqueante distinto, no un extra:
  //   ecommerce   → Pagos · Checkout · Envíos · Dominios · Marca · Notificaciones
  //   operaciones → ver y gestionar los pedidos que entran
  //   cod         → el formulario COD (el canal principal en Perú)
  //   logistics   → Transporte/agencias: sin esto no puede ENTREGAR
  // Lo que queda como upsell de `pro`: crm, whatsapp, loyalty, chatbot, finance, marketing…
  /*
   * DROP = el plan más chico que existe, y el único que NO es una tienda.
   *
   * Un revendedor de Smarty Drop no vende con Smarty: vende por Instagram o por su propio
   * WhatsApp y viene acá a ABASTECERSE. Darle POS, KDS o checkout sería regalarle el producto
   * que queremos venderle después; no dárselo y encima cobrarle no tendría sentido tampoco. El
   * plan es la puerta de entrada, no una versión mutilada de Smarty.
   *
   * 🪤 `operaciones` SE AGREGÓ EL 23-AGO DESPUÉS DE MIRAR LA PANTALLA. Yo había dicho que era
   *    «de tienda» y por eso la dejé afuera. Estaba mal: `operaciones` es donde vive la venta del
   *    revendedor A SU PROPIO CLIENTE —cargarla, verla, cobrarla— y ésa es su pantalla principal
   *    del día. Sin ella, la cuenta Drop podía comprar mercadería y no tenía dónde anotar a quién
   *    se la vendió. Es también lo que hace Aliclik: su menú abre en «Pedidos Contraentrega» con
   *    un botón «+ Nuevo pedido».
   *
   * `logistics` abre:
   *   Dropshipping → el catálogo de Smarty Drop, sus pedidos y sus pagos
   *   Transporte   → registrar su guía de Shalom, que por regla del modelo es SUYA
   *   Entregas     → seguir lo que ya despachó
   *
   * 🪤 Lo que ve de más NO se resuelve acá: el grupo «core» del menú (Inicio, Pedidos,
   *    Productos, Clientes, Contenido, Analíticas) no tiene `feature` y se muestra siempre.
   *    Ver el listado en la primera cuenta Drop antes de decidir qué se esconde: esconder de
   *    más deja al revendedor sin dónde ver lo que importó.
   */
  /*
   * DROP = el revendedor. Lo que NO tiene es tan importante como lo que sí:
   *
   *   sin `catalogo_propio` → no crea variantes ni ajusta existencias: el producto y el stock son
   *                           del almacén. El founder: «el dropshipper jala el producto tal cual».
   *   sin `analiticas`      → todavía no: se le prende cuando los reportes estén terminados.
   *   sin `flota_propia`    → despacha por agencia, no con motorizados suyos.
   *   sin `ecommerce`       → no tiene storefront, así que no hay pedidos web ni carritos. Vuelve
   *                           el día que conecte Shopify.
   *
   * 🪤 Y ESO SE PRENDE SIN DEPLOY: `plans.features` en la base manda sobre esta lista. Agregar
   *    'analiticas' a la fila del plan es un UPDATE, no un release.
   */
  drop: ['logistics', 'operaciones', 'avisos'],
  /*
   * ALMACEN = el plan del proveedor o almacén EXTERNO de Smarty Drop. No paga por serlo, así que no
   * se le regala la tienda: ve su cola de encargos, sus productos y su stock, y despacha.
   *
   *   operaciones     → Ventas y Entregas: la cola de encargos que tiene que preparar
   *   logistics       → Dropshipping (Mi almacén, Inventario, Liquidación) y Transporte
   *   catalogo_propio → sus productos, variantes y existencias: el stock es SUYO
   *
   * 🚨 EXISTÍA A MEDIAS HASTA EL 25-SEP. `fn_drop_alta_de_proveedor` ponía `plan_type = 'almacen'`
   *    y `RUTAS_DEL_ROL_ALMACEN` tenía su portón, pero esta lista no lo nombraba:
   *    `normalizePlanType('almacen')` devolvía `basic` y el almacén veía y abría la tienda entera.
   *    Lo que el menú y el portón muestran lo decide `RUTAS_DEL_ROL_ALMACEN`; esto decide los módulos.
   */
  almacen: ['operaciones', 'logistics', 'catalogo_propio'],
  basic: ['pos', 'kds', 'ecommerce', 'pedidos_online', 'operaciones', 'cod', 'logistics', 'proveedores',
          'catalogo_propio', 'analiticas', 'flota_propia', 'avisos'],
  // `call_center` acompaña a `crm` en los dos planes que lo tenían: separar la feature no le quita
  // nada a nadie. Quien hoy paga Pro o Enterprise sigue con las mismas 23 pantallas.
  pro: ['pos', 'kds', 'ecommerce', 'pedidos_online', 'marketplaces', 'loyalty', 'cod', 'operaciones', 'logistics', 'proveedores', 'crm', 'call_center', 'finance', 'whatsapp', 'chatbot',
        'catalogo_propio', 'analiticas', 'flota_propia', 'avisos'],
  enterprise: [
    'pos',
    'kds',
    'ecommerce',
    'pedidos_online',
    'marketplaces',
    'loyalty',
    'cod',
    'operaciones',
    'crm',
    'call_center',
    'marketing',
    'logistics',
    'proveedores',
    'commander',
    'finance',
    'inventory',
    'whatsapp',
    'chatbot',
    'ads',
    'accounting',
    'agents',
    'copilot',
    'discounts',
    'catalogo_propio',
    'analiticas',
    'flota_propia',
    'avisos',
  ],
};

/** Normaliza slugs de plan desde DB (mayúsculas, espacios, alias legacy). */
export function normalizePlanType(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === '') return 'basic';
  const p = String(raw).toLowerCase().trim();
  if (p === 'premium' || p === 'paid') return 'pro';
  if (p in PLAN_FEATURES) return p;
  return 'basic';
}

// Features habilitadas en modo desarrollo (todos los módulos)
export const DEV_MODE_FEATURES: FeatureName[] = Object.values(PLAN_FEATURES)
  .flat()
  .filter((feature, index, self) => self.indexOf(feature) === index); // Unique

/** Planes de menor a mayor rango. Fuente para derivar "el plan mínimo" de un módulo. */
export const PLAN_ORDER = ['drop', 'basic', 'pro', 'enterprise'] as const;

/**
 * El plan de menor rango que incluye este módulo (para mostrar "disponible en plan X").
 * Se DERIVA de PLAN_FEATURES — antes se hardcodeaba aparte en MODULES_INFO y divergía.
 */
export function minPlanForModule(key: FeatureName): string {
  for (const plan of PLAN_ORDER) {
    if (PLAN_FEATURES[plan]?.includes(key)) return plan;
  }
  return 'enterprise';
}
